import type {
    TileableModel,
    TileViewModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { CarouselSection } from '@rocco/ui/layout/components/CarouselSection';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import { cn } from '@rocco/utils/cn';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { useDirectoryBrowseTheme } from '~/ui-rocco/pages/directory-browse/directory-browse-theme';
import { isProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { isProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { TileOverlayWithFollowSelector } from '~/modules/moodboards/components';
import { buildTileActionInfo } from '~/modules/moodboards/utils/tile-action-info';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useCategoryRevamp } from '@modules/category/hook/use-category-revamp';
import {
    CategoryPageTileWithAdminActions,
    getPinType,
} from './CategoryPageTileWithAdminActions';

/**
 * Props interface for the CategoryPageRecentlyViewed component
 * @interface CategoryPageRecentlyViewedProps
 * @property {DisplayMode} displayMode - Current display mode (mobile/desktop)
 * @property {TileableModel[]} items - Array of recently viewed items to display
 */
interface CategoryPageRecentlyViewedProps {
    displayMode: DisplayMode;
    items: TileableModel[];
}

/**
 * Component that renders a horizontal scrollable carousel of recently viewed items
 * Shows items in a grid with different sizes for desktop/mobile.
 *
 * With the category revamp active the strip renders the uniform portrait card
 * through the shared CategoryPageTileWithAdminActions (enquiry-first action
 * panel, admin actions); the flag can only be on here on the revamp browse
 * pages — the legacy category pages render only when it is off — so the
 * pre-revamp markup below is unchanged for them.
 * @param {CategoryPageRecentlyViewedProps} props - Component props
 * @returns {React.JSX.Element | null} The rendered carousel section or null if no items
 */
export const CategoryPageRecentlyViewed = (
    props: CategoryPageRecentlyViewedProps,
) => {
    const { displayMode, items } = props;
    const isDesktop = displayMode === DisplayMode.Desktop;
    const { isInDesignBoard } = useHandleFollow();
    // The section title inherits its colour; flip it to white on the dark
    // projects browse theme (light pages / no provider default to light).
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const categoryRevamp = useCategoryRevamp();

    const isMobile = !isDesktop;
    const recentlyViewedTiles: TileViewModel<TileableModel>[] = items.map(
        (item) => ({
            // Mixed-type carousel: the uniform portrait card keeps every item
            // at the same height on the revamp pages. The portrait card is
            // light-theme only, so on the dark projects browse project tiles
            // use the page grid's `projectBrowse` card instead — the only
            // revamp card with a white footer.
            aspectRatio: categoryRevamp
                ? isDark && isProjectTileViewModel(item)
                    ? 'projectBrowse'
                    : 'portrait'
                : isProductTileViewModel(item)
                  ? 'square'
                  : 'default',
            ...(categoryRevamp && { rounded: false }),
            model: item,
            section: 'CategoryPageRecentlyViewed',
            compact: isMobile,
            isFollowed: isInDesignBoard(item.id, getPinType(item)),
            ...(categoryRevamp
                ? {}
                : {
                      overlay: (
                          <TileOverlayWithFollowSelector
                              pinId={item.id}
                              pinType={getPinType(item)}
                              compact={isMobile}
                              tileInfo={buildTileActionInfo(item)}
                          />
                      ),
                  }),
        }),
    );

    if (recentlyViewedTiles.length === 0) {
        return null;
    }

    return (
        <>
            {/* The rule the products page carries above its own recently-viewed
                block, so the three directories close the grid the same way. Its
                gutters match the carousel's own so the two line up. */}
            <div className="px-6 md:px-12">
                <hr
                    className={cn(
                        'm-0 h-0 border-t',
                        isDark ? 'border-offwhite/15' : 'border-[#e5e5e5]',
                    )}
                />
            </div>
            <CarouselSection
                title="Recently viewed"
                titleClassName={cn(
                    'text-6 font-medium tracking-[0.025rem] md:left-12',
                    isDark && 'text-white',
                )}
                size={isDesktop ? 'recentlyViewed' : 'small'}
                dragFree
            >
                {recentlyViewedTiles.map((tile) =>
                    categoryRevamp ? (
                        <CategoryPageTileWithAdminActions
                            key={`recentlyViewedTiles-${tile.model.id}`}
                            tile={tile}
                        />
                    ) : (
                        <Tile
                            key={`recentlyViewedTiles-${tile.model.id}`}
                            tile={tile}
                        />
                    ),
                )}
            </CarouselSection>
        </>
    );
};
